import { useEffect, useState } from 'react';
import { Crop } from 'react-image-crop';
import { message } from 'antd';
import { FormComponentProps } from '@ant-design/compatible/lib/form';
import { useDispatch } from 'react-redux';
import { scroller } from 'react-scroll';
import { appActions } from '../../../../../../../store/modules/app';
import { asyncSimpleModal } from '../../../../../../../lib/common';
import {
  generateUserWebCategoryPageUrl,
  generateUserWebEventPageUrl,
} from '../../../../../../../lib/utils/categoryUtils';
import { normalizeLinkUrl } from '../../../../../../../lib/utils/urlUtils';
import { EventSectionClickLayer } from '../../../../../../../types/query/event';
import { MAXIMUM_NUMBER_OF_EVENT_IMAGE_SECTION_AREA_LINK } from '../../../../../../../constants/service/app/eventConstants';

type CategoryItem = {
  id: number;
  parentId: number | null;
  productCategoryId: number;
  productCategoryName: string;
};
type SearchProductCategoryModalSubmitData = {
  mainProductCategory: CategoryItem;
  subProductCategory: CategoryItem[];
};

interface UseClickLayerUtilsProps extends FormComponentProps {
  name: string;
  value: EventSectionClickLayer[];
  onChange?: (clickLayer: EventSectionClickLayer[]) => void;
}
const useClickLayerUtils = ({ form, name, value, onChange }: UseClickLayerUtilsProps) => {
  const [isDrag, setDrag] = useState(false);
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [eventModalTargetIndex, setEventModalTargetIndex] = useState<number | null>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    const onDoubleClick = () => {
      if (!onChange) return;

      if (value && value.length === MAXIMUM_NUMBER_OF_EVENT_IMAGE_SECTION_AREA_LINK) {
        message.error(`링크 영역은 최대 ${MAXIMUM_NUMBER_OF_EVENT_IMAGE_SECTION_AREA_LINK}까지만 등록이 가능합니다.`);
        return;
      }

      onChange([
        ...value,
        {
          x: Math.round(crop.x * 100) / 100,
          y: Math.round(crop.y * 100) / 100,
          width: Math.round(crop.width * 100) / 100,
          height: Math.round(crop.height * 100) / 100,
          url: '',
        },
      ]);
      message.success('링크 영역이 추가되었습니다.');
      setCrop({ unit: '%', x: 0, y: 0, width: 0, height: 0 });
    };

    const cropSelection = document.querySelector('.ReactCrop__crop-selection');
    if (!isDrag && cropSelection) {
      cropSelection.addEventListener('dblclick', onDoubleClick);
    }

    return () => {
      if (cropSelection) {
        cropSelection.removeEventListener('dblclick', onDoubleClick);
      }
    };
  }, [isDrag, value, onChange, crop]);

  const appToggleModal = (args: any) => {
    dispatch(appActions.appToggleModal(args));
  };

  const onCropChange = (crop: Crop, percentCrop: Crop) => {
    const positiveIntegerPercentCrop = {
      ...percentCrop,
      x: Math.max(percentCrop.x, 0),
      y: Math.max(percentCrop.y, 0),
    };
    setCrop(positiveIntegerPercentCrop);
  };

  const onDeleteMapAreaLinkItem = (targetIndex: number) => {
    if (!onChange) return;
    onChange(value.filter((_: unknown, index: number) => index !== targetIndex));
  };

  const onChangeLinkUrl = (url: string, index: number) => {
    if (!onChange) return;
    // 사용자 직접 입력·자동완성 모달 채움 모두 이 함수를 통과하므로 여기서 한 번에 정규화
    const normalized = normalizeLinkUrl(url);
    onChange(value.map((item, _index) => (index === _index ? { ...item, url: normalized } : item)));
    form.setFieldsValue({
      [`${name}-clickLayers-link_${index}`]: normalized,
    });
  };

  const onSubmitSearchProductCategory = (data: SearchProductCategoryModalSubmitData, index: number) => {
    const { mainProductCategory, subProductCategory } = data;
    const subProductCategoryIdList = subProductCategory.map((item) => item.id);
    const targetUrl = generateUserWebCategoryPageUrl(mainProductCategory.id, subProductCategoryIdList);

    onChangeLinkUrl(targetUrl, index);
  };

  const openSearchProductCategoryModal = (index: number) => {
    appToggleModal({
      name: 'searchProductCategoryModal',
      data: {
        listData: {
          list: [],
        },
        setData: (data: SearchProductCategoryModalSubmitData) => onSubmitSearchProductCategory(data, index),
      },
    });
  };

  const scrollToMapAreaLinkTop = (targetIndex: number) => {
    scroller.scrollTo(`${name}-area-link-item-${targetIndex}`, {
      smooth: true,
      containerId: `${name}-img-wrap`,
    });
  };

  const onClickMapAreaLinkInfo = async (targetIndex: number) => {
    await asyncSimpleModal({
      type: 'info',
      title: `${targetIndex + 1} 링크 영역`,
      message: JSON.stringify(value[targetIndex], null, 2),
    });
  };

  const onSubmitSearchEvent = (eventId: number) => {
    if (eventModalTargetIndex === null) return;
    const targetUrl = generateUserWebEventPageUrl(eventId);
    onChangeLinkUrl(targetUrl, eventModalTargetIndex);
  };

  return {
    crop,
    setDrag,
    onCropChange,
    onDeleteMapAreaLinkItem,
    onChangeLinkUrl,
    openSearchProductCategoryModal,
    scrollToMapAreaLinkTop,
    onClickMapAreaLinkInfo,
    eventModalTargetIndex,
    setEventModalTargetIndex,
    onSubmitSearchEvent,
  };
};

export default useClickLayerUtils;
